// eslint-disable-next-line import/default
import SentryImport from '@sentry/browser';
import React, { FC, ReactNode } from 'react';
import { toast } from 'react-toastify';
export { LogicError } from '../../shared/error';
import { AccessError, AuthError, LogicError, ValidationError } from '../../shared/error';
import { RichMarkdownIt } from '../components';
import { TranslationKey, useTranslation } from '../translations';
import { APIError, RequestError } from './APIClient';

type SentryLogger = typeof SentryImport;

export function getSentry(): SentryLogger | undefined {
  return (window as { Sentry?: SentryLogger }).Sentry;
}

interface RedactedSMSFactor {
  id: number;
  lastDigits: string;
}

interface SecondFactorMinimalInfo {
  sms: RedactedSMSFactor[];
}

interface AccessErrorSection {
  permissions?: string[];
  code: AccessError;
  message: string;
}

export interface LogicErrorSection {
  code: LogicError;
  message: string;
  args?: Record<string, string[] | number | string>;
}

const logicErrorToMessage: Record<LogicError, TranslationKey> = {
  [LogicError.Test]: 'logic_error.test',
  [LogicError.None]: 'logic_error.none',
  [LogicError.RecordIsReferenced]: 'logic_error.record_is_referenced',
  [LogicError.CannotChangeDealerStatus]: 'logic_error.cannot_change_dealer_status',
  [LogicError.UserWasAlreadyAdded]: 'logic_error.user_was_already_added',
  [LogicError.CannotDeleteApplication]: 'logic_error.cannot_delete_application',
  [LogicError.CardDeclined]: 'logic_error.card_declined',
  [LogicError.EmailAlreadyInUse]: 'logic_error.email_already_in_use',
  [LogicError.CannotAddCompensationToOwn]: 'logic_error.cannot_add_compensation_to_own',
  [LogicError.FailedToPerformCashierPayment]: 'logic_error.failed_to_perform_cashier_payment',
  [LogicError.AttendeeHasErrors]: 'logic_error.attendee_has_errors',
  [LogicError.UserAlreadyHasRole]: 'logic_error.user_already_has_role',
  [LogicError.UserDoesNotHaveRole]: 'logic_error.user_does_not_have_role',
  [LogicError.InternalRole]: 'logic_error.internal_role',
  [LogicError.UserAlreadyAssignedToDepartment]: 'logic_error.user_already_assigned_to_department',
  [LogicError.AssistanceRequestAlreadySubmitted]:
    'logic_error.assistance_request_already_submitted',
  [LogicError.AssistanceRequestNotFound]: 'logic_error.assistance_request_not_found',
  [LogicError.UserHasNotRegistered]: 'logic_error.user_has_not_registered',
  [LogicError.CannotTargetSelf]: 'logic_error.cannot_target_self',
  [LogicError.IncorrectPassword]: 'logic_error.incorrect_password',
  [LogicError.PaymentFailed]: 'logic_error.payment_failed',
  [LogicError.ChildLimitExceeded]: 'logic_error.child_limit_exceeded',
  [LogicError.InvalidAttendanceType]: 'logic_error.invalid_attendance_type',
  [LogicError.RegistrationIsFull]: 'logic_error.registration_is_full',
  [LogicError.ProductNotAvailable]: 'logic_error.product_not_available',
  [LogicError.InvalidBadgeArt]: 'logic_error.invalid_badge_art',
  [LogicError.BadgeArtLimitReached]: 'logic_error.badge_art_limit_reached',
  [LogicError.InvalidConvention]: 'logic_error.invalid_convention',
  [LogicError.EmergencyContactRequired]: 'logic_error.emergency_contact_required',
  [LogicError.RegistrationClosed]: 'logic_error.registration_closed',
  [LogicError.ChildrenNotAllowed]: 'logic_error.children_not_allowed',
  [LogicError.TooManyChildren]: 'logic_error.too_many_children',
  [LogicError.ParentCannotVolunteer]: 'logic_error.parent_cannot_volunteer',
  [LogicError.OAuthMissingToken]: 'logic_error.o_auth_missing_token',
  [LogicError.OAuthInvalidScope]: 'logic_error.o_auth_invalid_scope',
  [LogicError.ApplicationNotGranted]: 'logic_error.application_not_granted',
  [LogicError.OAuthInvalidRedirectURI]: 'logic_error.o_auth_invalid_redirect_uri',
  [LogicError.DealerAlreadyAdded]: 'logic_error.dealer_already_added',
  [LogicError.InvalidPaymentProcessor]: 'logic_error.invalid_payment_processor',
  [LogicError.CannotUpdateDateOfBirth]: 'logic_error.cannot_update_date_of_birth',
  [LogicError.DuplicateOrderNumber]: 'logic_error.duplicate_order_number',
  [LogicError.NotDepartmentLead]: 'logic_error.not_department_lead',
  [LogicError.GatewayOperationNotSupported]: 'logic_error.gateway_operation_not_supported',
  [LogicError.CannotRedeemVoucherNegative]: 'logic_error.cannot_redeem_voucher_negative',
  [LogicError.InvalidVoucher]: 'logic_error.invalid_voucher',
  [LogicError.VoucherAlreadyRedeemed]: 'logic_error.voucher_already_redeemed',
  [LogicError.GrantWithRefIdAlreadyExists]: 'logic_error.grant_with_ref_id_alreadyExists',
  [LogicError.RegistrationCategoryAlreadyExists]:
    'logic_error.registration_category_already_exists',
  [LogicError.RefundQuantityExceedsRemaining]: 'logic_error.refund_quantity_exceeds_remaining',
  [LogicError.InvalidRefundRequestStatus]: 'logic_error.invalid_refund_request_status',
  [LogicError.DataOutOfSync]: 'logic_error.data_out_of_sync',
  [LogicError.RefundOperationFailed]: 'logic_error.refund_operation_failed',
  [LogicError.InvalidRefundAmount]: 'logic_error.invalid_refund_amount',
  [LogicError.OrderNotPaid]: 'logic_error.order_not_paid',
  [LogicError.VoucherCodeExists]: 'logic_error.voucher_code_exists',
  [LogicError.CannotDeletePaidFee]: 'logic_error.cannot_delete_paid_fee',
  [LogicError.NoRefundItemsProvided]: 'logic_error.no_refund_items_provided',
  [LogicError.EventStoreCannotUseCategory]: 'logic_error.event_store_cannot_use_category',
  [LogicError.OrderCannotBeCancelled]: 'logic_error.order_cannot_be_cancelled',
  [LogicError.InsufficientStock]: 'logic_error.insufficient_stock',
  [LogicError.SecondFactorPhoneInUse]: 'logic_error.second_factor_phone_in_use',
  [LogicError.InvalidFactorIdOrVerification]: 'logic_error.invalid_factor_id_or_verification',
  [LogicError.MissingRequiredProductOption]: 'logic_error.missing_required_product_option',
  [LogicError.InvalidOptionValue]: 'logic_error.invalid_option_value',
  [LogicError.UnknownProductOption]: 'logic_error.unknown_product_option',
  [LogicError.MustProvideSelectOption]: 'logic_error.must_provide_select_option',
  [LogicError.InternalOption]: 'logic_error.internal_option',
  [LogicError.AgeRestrictedProduct]: 'logic_error.age_restricted_product',
  [LogicError.RoleRestrictedProduct]: 'logic_error.role_restricted_product',
  [LogicError.ProductNoMultipleTax]: 'logic_error.product_no_multiple_tax',
  [LogicError.WrongPlatformFeeAssignment]: 'logic_error.wrong_platform_fee_assignment',
  [LogicError.RaffleAlreadyClosed]: 'logic_error.raffle_already_closed',
  [LogicError.ProductInAnotherRaffle]: 'logic_error.product_in_another_raffle',
  [LogicError.RaffleTicketRescinded]: 'logic_error.raffle_ticket_rescinded',
  [LogicError.AlreadyRedeemedError]: 'logic_error.already_redeemed_error',
  [LogicError.RegistrationNotDeletable]: 'logic_error.registration_not_deletable',
  [LogicError.GeoCodingError]: 'logic_error.geo_coding_error',
  [LogicError.TooFrequentEmailChange]: 'logic_error.too_frequent_email_change',
  [LogicError.DealerAreaClosed]: 'logic_error.dealer_area_closed',
  [LogicError.NoRegistrationGrantTransfer]: 'logic_error.no_registration_grant_transfer',
  [LogicError.TargetEmailNotFound]: 'logic_error.target_email_not_found',
  [LogicError.TargetRegistrationHasGrant]: 'logic_error.target_registration_has_grant',
  [LogicError.VolunteerAlreadyAssigned]: 'logic_error.volunteer_already_assigned',
  [LogicError.DigitalGrantNotOwnedError]: 'logic_error.digital_grant_not_owned_error',
  [LogicError.CannotUpdateVolunteerOption]: 'logic_error.cannot_update_volunteer_option',
  [LogicError.OrderIsPaid]: 'logic_error.order_is_paid',
  [LogicError.NotAVendor]: 'logic_error.not_a_vendor',
  [LogicError.VendorIsNotApproved]: 'logic_error.vendor_is_not_approved',
  [LogicError.SessionAlreadyVerified]: 'logic_error.session_already_verified',
  [LogicError.DateOfBirthMissing]: 'logic_error.date_of_birth_missing',
  [LogicError.IdentityInputMissing]: 'logic_error.identity_input_missing',
  [LogicError.RequiredIdentityMissing]: 'logic_error.required_identity_missing',
  [LogicError.AgeVerification]: 'logic_error.age_verification',
  [LogicError.CanNotUpdateVerifiedInformation]: 'logic_error.can_not_update_verified_information',
  [LogicError.IdentitySessionAlreadyClaimed]: 'logic_error.identity_session_already_claimed',
  [LogicError.IdentitySessionNotVerified]: 'logic_error.identity_session_not_verified',
  [LogicError.CannotOversellProduct]: 'logic_error.cannot_oversell_product',
  [LogicError.OrderAlreadyPaid]: 'logic_error.order_already_paid',
  [LogicError.TicketAlreadyBought]: 'logic_error.ticket_already_bought',
  [LogicError.NoTicketGroupsAvailable]: 'logic_error.no_ticket_groups_available',
  [LogicError.ProductStockTooSmall]: 'logic_error.product_stock_too_small',
  [LogicError.TooManyTickets]: 'logic_error.too_many_tickets',
  [LogicError.ProductRequiresGrant]: 'logic_error.product_requires_grant',
  [LogicError.VolunteerClosed]: 'logic_error.volunteer_closed',
  [LogicError.DealersClosed]: 'logic_error.dealers_closed',
  [LogicError.ProductVolumeError]: 'logic_error.product_volume_error',
  [LogicError.AddonsCanNotHaveAddonsError]: 'logic_error.addons_can_not_have_addonsError',
  [LogicError.AddonItselfError]: 'logic_error.addon_itself_error',
  [LogicError.AddonRegistrationError]: 'logic_error.addon_registration_error',
  [LogicError.ExportRequestAlreadyCreated]: 'logic_error.export_request_already_created',
  [LogicError.ExportIsExpired]: 'logic_error.export_is_expired',
  [LogicError.NoDataToExportError]: 'logic_error.no_data_to_export_error',
  [LogicError.ExportRequestAlreadyApproved]: 'logic_error.export_request_already_approved',
  [LogicError.ExportRequestIsRejected]: 'logic_error.export_request_is_rejected',
  [LogicError.ExportRequestNotApproved]: 'logic_error.export_request_not_approved',
  [LogicError.DirectApprovalNotAllowed]: 'logic_error.direct_approval_not_allowed',
  [LogicError.ProductAlreadyInOrder]: 'logic_error.product_already_in_order',
  [LogicError.BadPaginationToken]: 'logic_error.bad_pagination_token',
  [LogicError.AddonAlreadyExistsError]: 'logic_error.addon_already_exists_error',
  [LogicError.VendorAddonMustBeInSameCategory]: 'logic_error.vendor_addon_must_be_inSameCategory',
  [LogicError.CannotRemoveAddonFromCart]: 'logic_error.cannot_remove_addon_from_cart',
  [LogicError.InternalRegistrationFlag]: 'logic_error.internal_registration_flag',
  [LogicError.ExportUserApprovedSelf]: 'logic_error.export_user_approved_self',
  [LogicError.VoucherProductRestricted]: 'logic_error.voucher_product_restricted',
  [LogicError.EmptyCart]: 'logic_error.empty_cart',
  [LogicError.OutstandingBalance]: 'logic_error.outstanding_balance',
  [LogicError.CategoryAssignedToDealerArea]: 'logic_error.category_assigned_to_dealer_area',
  [LogicError.DiscourseSignatureError]: 'logic_error.discourse_signature_error',
  [LogicError.DiscourseMissingNonce]: 'logic_error.discourse_missing_nonce',
  [LogicError.OptionIncorrectSource]: 'logic_error.option_incorrect_source',
  [LogicError.RequestEntityTooLarge]: 'logic_error.request_entity_too_large',
  [LogicError.CantTransferBadgePrinted]: 'logic_error.cant_transfer_badge_printed',
  [LogicError.ProductNotFound]: 'logic_error.product_not_found',
  [LogicError.ImageFormatNotSupported]: 'logic_error.image_format_not_supported',
  [LogicError.InvalidImage]: 'logic_error.invalid_image',
  [LogicError.ProductGrantQuantityLimit]: 'logic_error.product_grant_quantity_limit',
  [LogicError.RefundNoAction]: 'logic_error.refund_no_action',
  [LogicError.NoDeleteRegistrationCategory]: 'logic_error.no_delete_registration_category',
  [LogicError.TransferDisabled]: 'logic_error.transfer_disabled',
  [LogicError.VoucherCannotBeUsedWithOtherVouchers]:
    'logic_error.voucher_cannot_be_used_withOtherVouchers',
  [LogicError.InvalidEmailDomain]: 'logic_error.invalid_email_domain',
  [LogicError.InvalidEmailSender]: 'logic_error.invalid_email_sender',
  [LogicError.OrderGrantError]: 'logic_error.order_grant_error',
  [LogicError.UserMissingPermissions]: 'logic_error.user_missing_permissions',
  [LogicError.RefundsDisabled]: 'logic_error.refunds_disabled',
  [LogicError.VoucherExpired]: 'logic_error.voucher_expired',
  [LogicError.TooManyAssistants]: 'logic_error.too_many_assistants',
  [LogicError.IncorrectUser]: 'logic_error.incorrect_user',
  [LogicError.OptionTypeChange]: 'logic_error.option_type_change',
  [LogicError.ApplicationAlreadyApproved]: 'logic_error.application_already_approved',
  [LogicError.DealerNotAdmin]: 'logic_error.dealer_not_admin',
  [LogicError.InvalidAddonDealerRequest]: 'logic_error.invalid_addon_dealer_request',
  [LogicError.TemplateCannotBeParsed]: 'logic_error.template_cannot_be_parsed',
  [LogicError.TemplateRequired]: 'logic_error.template_required',
  [LogicError.TemplateVariablesMissing]: 'logic_error.template_variables_missing',
  [LogicError.NotAllowedToEditRegisterEmailTemplate]:
    'logic_error.not_allowed_to_edit_registerEmailTemplate',
  [LogicError.ModelOverLimit]: 'logic_error.model_over_limit',
  [LogicError.OverSublimit]: 'logic_error.over_sublimit',
  [LogicError.InvalidExpiration]: 'logic_error.invalid_expiration',
  [LogicError.InvalidDeviceType]: 'logic_error.invalid_device_type',
  [LogicError.DeviceAlreadyRegistered]: 'logic_error.device_already_registered',
  [LogicError.QuikTicketDisabled]: 'logic_error.quik_ticket_disabled',
  [LogicError.PaymentGatewayNotSetUp]: 'logic_error.payment_gateway_not_set_up',
  [LogicError.PaymentGatewayValidationWouldViolate]:
    'logic_error.payment_gateway_validation_would_violate',
  [LogicError.PaymentGatewayApiCallFailed]: 'logic_error.payment_gateway_api_call_failed',
  [LogicError.PaymentGatewayResponseIncomplete]: 'logic_error.payment_gateway_response_incomplete',
  [LogicError.UsernameTaken]: 'logic_error.username_taken',
  [LogicError.TaxMustBePercent]: 'logic_error.tax_must_be_percent',
  [LogicError.HotelReleaseGroupEntriesClosed]: 'logic_error.hotel_release_group_entries_closed',
  [LogicError.HotelReleaseEntryNotEligible]: 'logic_error.hotel_release_entry_not_eligible',
  [LogicError.HotelIntegrationManualSyncNotAllowedError]:
    'logic_error.hotel_integration_manual_sync_notAllowedError',
  [LogicError.HotelReservationTransferNotAllowedError]:
    'logic_error.hotel_reservation_transfer_not_allowedError',
  [LogicError.CannotUpdateProductOption]: 'logic_error.cannot_update_product_option',
  [LogicError.DealerOptionLockedError]: 'logic_error.dealer_option_locked_error',
  [LogicError.RoleInviteExpired]: 'logic_error.role_invite_expired',
  [LogicError.RoleInviteMaxUses]: 'logic_error.role_invite_max_uses',
  [LogicError.ProductWithAddonsQuantityError]: 'logic_error.product_with_addons_quantity_error',
  [LogicError.OnlySearchOrFilter]: 'logic_error.only_search_or_filter',
  [LogicError.OwningAccountNoRegistration]: 'logic_error.owning_account_no_registration',
  [LogicError.CannotRequestDeleteSelf]: 'logic_error.cannot_request_delete_self',
  [LogicError.CannotDeleteAdmin]: 'logic_error.cannot_delete_admin',
  [LogicError.DeletionRequestExists]: 'logic_error.deletion_request_exists',
  [LogicError.UserDoesNotExist]: 'logic_error.user_does_not_exist',
  [LogicError.NoPendingDeletionRequest]: 'logic_error.no_pending_deletion_request',
  [LogicError.BadTaskState]: 'logic_error.bad_task_state',
  [LogicError.NoUsernameError]: 'logic_error.no_username_error',
  [LogicError.NoEmailError]: 'logic_error.no_email_error',
  [LogicError.BadgeNameIsRequired]: 'logic_error.badge_name_is_required',
  [LogicError.SubAccountRestricted]: 'logic_error.sub_account_restricted',
  [LogicError.UnderageProfile]: 'logic_error.underage_profile',
  [LogicError.ExportRequestAlreadyExists]: 'logic_error.export_request_already_exists',
  [LogicError.CannotClearBeforeConEnd]: 'logic_error.cannot_clear_before_con_end',
  [LogicError.MalformedRequest]: 'logic_error.malformed_request',
  [LogicError.NoUpgradePath]: 'logic_error.no_upgrade_path',
  [LogicError.AttendeesCannotEditOIO]: 'logic_error.attendees_cannot_edit_o_iO',
  [LogicError.CannotDirectEditRegOIO]: 'logic_error.cannot_direct_edit_reg_oIO',
};

const authErrorToMessage: Record<AuthError, TranslationKey> = {
  [AuthError.NotAuthenticated]: 'auth_error.not_authenticated',
  [AuthError.IncorrectLogin]: 'auth_error.incorrect_login',
  [AuthError.NotVerified]: 'auth_error.not_verified',
  [AuthError.PasswordResetRequired]: 'auth_error.password_reset_required',
  [AuthError.UnderReview]: 'auth_error.under_review',
  [AuthError.Banned]: 'auth_error.banned',
  [AuthError.MaintenanceMode]: 'auth_error.maintenance_mode',
  [AuthError.SecondFactorMissing]: 'auth_error.second_factor_missing',
  [AuthError.SecondFactorInvalid]: 'auth_error.second_factor_invalid',
  [AuthError.Invalid]: 'auth_error.invalid',
  [AuthError.Expired]: 'auth_error.expired',
  [AuthError.Revoked]: 'auth_error.revoked',
  [AuthError.CSRFMissingHeader]: 'auth_error.csrf_missing_header',
  [AuthError.CSRFMissingCookie]: 'auth_error.csrf_missing_cookie',
  [AuthError.CSRFMismatch]: 'auth_error.csrf_mismatch',
};

const accessErrorToMessage: Record<AccessError, TranslationKey> = {
  [AccessError.Test]: 'access_error.test',
  [AccessError.MissingCredentials]: 'access_error.missing_credentials',
  [AccessError.InvalidAuthHeader]: 'access_error.invalid_auth_header',
  [AccessError.InvalidCredentials]: 'access_error.invalid_credentials',
  [AccessError.TokenNotBelongToUser]: 'access_error.token_not_belong_to_user',
  [AccessError.RedirectTokenNotFound]: 'access_error.redirect_token_not_found',
  [AccessError.BannedFromDealing]: 'access_error.banned_from_dealing',
  [AccessError.UnauthorizedBadgeData]: 'access_error.unauthorized_badge_data',
  [AccessError.UserNotAllowedDiscount]: 'access_error.user_not_allowed_discount',
  [AccessError.DiscountNotBelongToProduct]: 'access_error.discount_not_belong_to_product',
  [AccessError.UserNotDepartmentLead]: 'access_error.user_not_department_lead',
  [AccessError.BannedFromVolunteering]: 'access_error.banned_from_volunteering',
  [AccessError.CannotChangeOwnRoles]: 'access_error.cannot_change_own_roles',
  [AccessError.CannotChangeAdminRole]: 'access_error.cannot_change_admin_role',
  [AccessError.UserNotAllowedAction]: 'access_error.user_not_allowed_action',
  [AccessError.GatewayNotFound]: 'access_error.gateway_not_found',
  [AccessError.EndpointRequiresRegisteredDevice]:
    'access_error.endpoint_requires_registered_device',
  [AccessError.NoPermissionToAccessCategory]: 'access_error.no_permission_to_access_category',
  [AccessError.MissingPermissions]: 'access_error.missing_permissions',
  [AccessError.MalformedSupportToken]: 'access_error.malformed_support_token',
  [AccessError.SupportTokenIPMismatch]: 'access_error.support_token_ip_mismatch',
  [AccessError.InvalidSupportToken]: 'access_error.invalid_support_token',
  [AccessError.UnknownDealerToken]: 'access_error.unknown_dealer_token',
  [AccessError.MissingQuickTicketPermissions]: 'access_error.missing_quick_ticket_permissions',
  [AccessError.ConventionHostMismatch]: 'access_error.convention_host_mismatch',
  [AccessError.DealerRequestNotFound]: 'access_error.dealer_request_not_found',
  [AccessError.UnauthorizedOAuthScopes]: 'access_error.unauthorized_oauth_scopes',
};

export interface ErrorResponse {
  errors: {
    server?: {
      hash: string;
    };
    access?: AccessErrorSection;
    logic?: LogicErrorSection[];
    resource?: {
      message: string;
      name: string;
    };
    usage?: string;
    authentication?: {
      code: AuthError;
      message: string;
      secondFactor?: SecondFactorMinimalInfo;
    };
    validation?: ValidationError[];
    rateLimit?: {
      message: string;
      expiresAt: number;
    };
    internal?: string[];
  };
}

export function isAPIErrorResponse(res: unknown): res is ErrorResponse {
  return typeof res === 'object' && !!(res as ErrorResponse).errors;
}

function buildDetails(err: Error): Record<string, unknown> {
  const extra: Record<string, unknown> = {
    // Deliberately spreading we only want enumerable properties
    // eslint-disable-next-line @typescript-eslint/no-misused-spread
    Error: { ...err },
  };

  for (const k of Object.keys(err)) {
    const val = (err as unknown as Record<string, string>)[k];
    extra[k] = val;
  }

  return extra;
}

class NonErrorError extends Error {
  public override name = 'NonErrorError';
  public constructor(public readonly value: unknown) {
    super(`Received non error object ${typeof value} ${String(value)}`);
  }
}

const UnknownError: FC = () => {
  const { ts } = useTranslation();
  return ts('unknown_error_please_contact_host');
};

function submitError(err: unknown, display: ErrorDisplay): string | undefined {
  const err2 = err instanceof Error ? err : new NonErrorError(err);
  let id: string | undefined;
  try {
    const extra = buildDetails(err2);
    const sntry = getSentry();
    if (sntry) {
      sntry.withScope((scope) => {
        scope.setExtras(extra);
        id = sntry.captureException(err2);
      });

      return id;
    }

    display(<UnknownError />);

    // eslint-disable-next-line no-console
    console.error(err2, extra);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to log', err, err2, error);
  }

  return undefined;
}

const ErrorLister: FC<{
  readonly errorResponse: ErrorResponse;
}> = ({ errorResponse: { errors } }) => {
  const { ts, tsa } = useTranslation();
  const list: React.JSX.Element[] = [];
  const append = (str: ReactNode) => {
    list.push(<li key={list.length}>{str}</li>);
  };

  const {
    validation,
    access,
    logic,
    usage,
    resource,
    authentication,
    server,
    rateLimit,
    internal,
  } = errors;

  if (validation) {
    for (const err of validation) {
      append(
        tsa(`validation.rule.${err.type}`, {
          ...(err.context as Record<string, string>),
          // eslint-disable-next-line unicorn/prefer-at
          label: err.path[err.path.length - 1] ?? ts('common.unknown'),
        }),
      );
    }
  }

  if (access) {
    const message = ts(accessErrorToMessage[access.code]);
    append(
      access.permissions ? ts('missing_permissions_list', access.permissions.join(',')) : message,
    );
  }

  if (logic) {
    for (const err of logic) {
      append(tsa(logicErrorToMessage[err.code], err.args ?? {}));
      if (err.args) {
        append(
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          Object.entries(err.args).reduce((msg, [key, value]) => `${msg}${key}: ${value}`, ''),
        );
      }
    }
  }

  if (server) {
    append(ts('server_error_report', server.hash));
  }

  if (usage) {
    throw new Error(`Usage Error ${usage}`);
  }

  if (resource) {
    append(resource.message || `Unable to find this ${resource.name}`);
  }

  if (authentication) {
    append(
      <>
        <RichMarkdownIt source={ts(authErrorToMessage[authentication.code])} /> (
        {authentication.code})
      </>,
    );
  }

  if (rateLimit) {
    append(ts('rate_limit_error', Math.ceil(rateLimit.expiresAt - Date.now() / 1000)));
  }

  if (internal) {
    for (const err of internal) {
      append(err);
    }
  }

  return (
    <div>
      <p>
        <strong>{ts('one_or_more_errors_occurred_while_trying_to_process_your_request')}</strong>
      </p>
      <ul>{list}</ul>
    </div>
  );
};

type ErrorDisplay = (content: React.JSX.Element) => void;

/**
 * Function that should be called with error.
 * @returns `false` if the error wasn't an expected app error.
 */
export type ErrorCapturer = (err: Error) => string | undefined;

export function setUserContext(userId: number): void {
  const sntry = getSentry();
  if (sntry) {
    sntry.setUser({
      id: userId.toString(),
    });
  }
}

const showError = (content: React.JSX.Element) => {
  toast.error(content);
};

export const captureError = (err: APIError | Error | RequestError): string | undefined => {
  if (err instanceof APIError) {
    showError(<ErrorLister errorResponse={err.apiResponse} />);
    return;
  }

  return submitError(err, showError);
};
